import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import BriefSection from "../components/brief/BriefSection";
import BriefHistory from "../components/brief/BriefHistory";

const errText = (e) => e?.response?.data?.detail?.message || "Could not load the brief.";
const isOff = (e) => e?.response?.data?.detail?.code === "phase_disabled";   // Construction IQ (Phase 8) switched off

// B3 — Morning Owner Command Brief: deterministic, cited, freshness-labelled. AI wording is off (cost-cap batch).
export default function OwnerBrief() {
  const [brief, setBrief] = useState(undefined);
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [off, setOff] = useState(false);
  const loadHistory = useCallback(() => api.get("/owner/brief/history").then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/owner/brief/latest").then((r) => { setBrief(r.data.brief); setNote(r.data.schedule || ""); })
      .catch((e) => { if (isOff(e)) setOff(true); else setErr(errText(e)); setBrief(null); });
    loadHistory();
  }, [loadHistory]);
  const open = (id) => api.get(`/owner/brief/${id}`).then((r) => setBrief(r.data)).catch((e) => setErr(errText(e)));
  const regenerate = async () => {
    setBusy(true); setErr("");
    try { setBrief((await api.post("/owner/brief/regenerate")).data); loadHistory(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };
  return (
    <AppShell title="Morning Owner Command Brief" subtitle="Today's jobs, schedule, risk, money, approvals and field activity — every line cites its records">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-6 grid gap-6 lg:grid-cols-[1fr_280px]" data-testid="owner-brief-page">
        <div className="space-y-4 min-w-0">
          {err && <p role="alert" className="text-sm text-red-700" data-testid="owner-brief-error">{err}</p>}
          {brief === undefined && <p className="font-mono text-xs" data-testid="owner-brief-loading">Loading…</p>}
          {off && (
            <p role="status" className="text-sm bg-erf-creamSecondary border border-erf-borderCream rounded p-3" data-testid="owner-brief-module-off">
              Construction IQ is turned off for this company, so no morning brief is generated. The Founder can turn it on in{" "}
              <Link to="/admin/settings/modules" className="underline" data-testid="owner-brief-modules-link">Settings → Modules</Link>.
            </p>
          )}
          {brief === null && !err && !off && <p className="text-sm" data-testid="owner-brief-none">No brief yet. {note}</p>}
          {brief && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm" data-testid="owner-brief-meta">Brief for {brief.brief_date} ({brief.timezone}) · {brief.trigger === "scheduled" ? "morning run" : "regenerated on demand"}</p>
                <Button type="button" onClick={regenerate} disabled={busy} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold" data-testid="owner-brief-regenerate">
                  {busy ? "Regenerating…" : "Regenerate now"}
                </Button>
              </div>
              {brief.stale && <p role="status" className="text-sm bg-amber-50 border border-amber-300 rounded p-3" data-testid="owner-brief-stale">Stale: {brief.stale_reason} Regenerate for current data.</p>}
              <p className="text-xs text-erf-dark/70" data-testid="owner-brief-ai-note">{brief.ai_wording?.note} Not distributed to anyone.</p>
              {brief.sections.map((s) => <BriefSection key={s.key} s={s} stale={brief.stale} />)}
            </>
          )}
          {brief === null && !err && !off && (
            <Button type="button" onClick={regenerate} disabled={busy} className="min-h-[44px] bg-erf-gold text-erf-dark font-semibold" data-testid="owner-brief-generate-first">{"Generate today's brief"}</Button>
          )}
        </div>
        <aside aria-labelledby="brief-history-title" className="space-y-2">
          <h2 id="brief-history-title" className="font-display font-semibold">Past briefs</h2>
          <BriefHistory rows={history} currentId={brief?.id} onOpen={open} />
        </aside>
      </div>
    </AppShell>
  );
}
